import { QueryClient } from "@tanstack/react-query"
import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister"
import type { PersistedClient } from "@tanstack/react-query-persist-client"

// Cache keys that are safe to write to localStorage — config / org data
// only, no patient PHI and no short-lived secrets. Anything not listed here
// (schedules, patient rosters, ROI metrics, nurse assignments) is held in
// memory only.
//
// Any query persisted here MUST be enabled at mount time. Hooks that fetch
// their own prerequisites inside queryFn satisfy this naturally; hooks that
// `enabled: !!someDependency` (where the dependency itself is another query)
// do not — the enabled-flip is not a mount event, so `refetchOnMount` won't
// fire and a stale snapshot from a previous session would be served forever.
const PERSISTABLE_QUERY_KEYS = new Set<string>([
	"permissions",
	"user-role",
	"user",
	"settings",
	"centers"
])

export const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			// Treat data as fresh for 5 minutes; eliminates the refetch-on-every-mount
			// thrash that the default `staleTime: 0` causes when navigating between
			// pages that hit the same endpoints.
			staleTime: 1000 * 60 * 5,
			// Keep results around for 30 minutes after the last consumer unmounts.
			gcTime: 1000 * 60 * 30,
			// Tabbing away and back should not invalidate dashboard data.
			refetchOnWindowFocus: false,
			refetchOnReconnect: "always",
			// Retry once on transient failures; default of 3 makes errors take 6+ seconds
			// to surface, which is worse UX than failing fast.
			retry: 1
		}
	}
})

export const queryPersister = createSyncStoragePersister({
	storage: typeof window !== "undefined" ? window.localStorage : undefined,
	key: "oncare-query-cache",
	throttleTime: 1000
})

export const shouldPersistQuery = (queryKey: readonly unknown[]): boolean => {
	const first = queryKey[0]
	return typeof first === "string" && PERSISTABLE_QUERY_KEYS.has(first)
}

export const dehydrateOptions = {
	shouldDehydrateQuery: (query: { queryKey: readonly unknown[] }) =>
		shouldPersistQuery(query.queryKey)
}

export type { PersistedClient }
